<template>
  <span class="location-search">
    <el-select
      class="autocomplete-input"
      v-model="value"
      filterable
      remote
      placeholder="可搜索地址或经纬度"
      :remote-method="lazySearch"
      :loading="loading"
    >
      <template slot="prefix">
        <i class="el-icon-search"></i>
      </template>
      <el-option v-for="item in items" :key="item.name" :value="item.name">
        <div class="item-container">
          <div class="item-left">
            {{ item.name }}
            <span class="desc">{{ item.desc }}</span>
          </div>
          <div class="item-right">
            <i
              v-if="item.district"
              class="el-icon-crop"
              @click="drawDistrict(item)"
            ></i>
            <i class="el-icon-location" @click="drawPoint(item)"></i>
          </div>
        </div>
      </el-option>
    </el-select>
  </span>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import latlngParse from './latlng-parse';
/** 高德位置搜索 */
const AMap = window.AMap;
const AMAP_DISTRICT_LEVEL_DICT = {
  country: 'country',
  province: 'province',
  city: 'district',
  district: 'county'
};
const DISTRICT_ZOOM = {
  country: 5, // 国
  province: 7, // 省
  district: 9, // 市
  county: 10 // 县/区
};
const CHINA_DISTRICT_LEVEL = ['省', '区', '市', '县', '旗', '盟', '州'];

export default {
  computed: {},
  data() {
    return {
      loading: false,
      value: '',
      items: []
    };
  },
  watch: {
    value(val) {
      const selected = this.items.find(item => item.name === val);
      this.$emit('select', selected);
    }
  },
  created() {
    this.lazySearch = debounce(this.querySearch, 500);
  },
  methods: {
    startswith(s, prefix) {
      const reg = new RegExp('^' + prefix);
      try {
        return s.search(reg) !== -1;
      } catch (e) {
        return false;
      }
    },
    async querySearch(keyword) {
      this.loading = true;
      const [latlngRes, addressRes] = await Promise.all([
        this.searchLatLng(keyword),
        this.searchAddress(keyword).catch(err => console.log(err))
      ]);
      const items = [];
      if (latlngRes) {
        items.push(latlngRes);
      }
      if (!isEmpty(addressRes)) {
        items.push(...addressRes);
      }
      // 补充行政区边界
      await Promise.all(
        items.map(async item => {
          if (
            item.name &&
            CHINA_DISTRICT_LEVEL.includes(item.name[item.name.length - 1])
          ) {
            try {
              const district = await this.searchDistrict(item.name, item.desc);
              item.district = district;
            } catch (e) {
              console.log('searchDistrict error', e);
            }
          }
        })
      );
      console.log('search', items);
      this.loading = false;
      this.items = items;
    },
    searchAddress(keyword) {
      return new Promise((resolve, reject) => {
        AMap.service(['AMap.Autocomplete'], () => {
          var auto = new AMap.Autocomplete();
          auto.search(keyword, (status, result) => {
            if (['error', 'no_data'].includes(status)) {
              return reject(new Error(status));
            }
            /** 处理没返回的location的数据 */
            const containLocationTips = result.tips.filter(
              tip => !!tip.location
            );
            const items = [];
            result.tips.forEach(tip => {
              const locationTip = containLocationTips.find(loc =>
                this.startswith(loc.district, tip.district)
              );
              if (!locationTip) {
                return;
              }
              tip.location = locationTip.location;
              items.push({
                searchType: 'address',
                name: tip.name,
                desc: tip.district,
                center: tip.location
              });
            });
            return resolve(items);
          });
        });
      });
    },
    searchLatLng(keyword) {
      const res = latlngParse(keyword);
      if (!res) {
        return Promise.resolve(null);
      }
      const [lat, lng] = res.values;
      return Promise.resolve({
        searchType: 'latlng',
        name: `${Math.abs(lat)}°${lat < 0 ? 'S' : 'N'} ${Math.abs(lng)}°${
          lng < 0 ? 'W' : 'E'
        }`,
        desc: '',
        center: { lat, lng }
      });
    },
    searchDistrict(keyword, desc, extensions = 'all') {
      return new Promise((resolve, reject) => {
        AMap.service(['AMap.DistrictSearch'], function() {
          var district = new AMap.DistrictSearch({
            // all: 返回行政区边界坐标点; base: 不返回;
            extensions,
            // 不返回下级行政区
            subdistrict: 0
          });
          district.search(keyword, function(status, result) {
            if (['error', 'no_data'].includes(status)) {
              reject(status);
              return;
            }
            // status is complete
            // 滤掉商圈(biz_area)，国家(country)
            var districtList = result.districtList.filter(
              district => !['biz_area', 'country'].includes(district.level)
            );
            if (isEmpty(districtList)) {
              // no data
              resolve([]);
            }
            districtList[0].level =
              AMAP_DISTRICT_LEVEL_DICT[districtList[0].level];
            var zoom = DISTRICT_ZOOM[districtList[0].level];
            // 中心点
            var center = {
              lon: districtList[0].center.lng,
              lat: districtList[0].center.lat
            };
            var uri = 'ad:' + districtList[0].adcode;
            var districtObj = {
              ...districtList[0],
              zoom,
              center,
              desc,
              uri
            };
            // 边界
            if (!isEmpty(districtObj.boundaries)) {
              let lonlats_list = districtObj.boundaries.map(boundary => {
                return boundary.map(c => {
                  return [c.lng, c.lat].map(lonlat => {
                    return parseFloat(lonlat.toFixed(5));
                  });
                });
              });
              delete districtObj.boundaries;
              // districtObj.lonlats_list = lonlats_list;
              // 因为高德返回的多边形数据尾端未封闭, 所以对各环额外填上一个点.
              lonlats_list = lonlats_list.map(lonlats => {
                lonlats.push(lonlats[0]);
                return [lonlats];
              });
              districtObj.geometry = {
                coordinates: lonlats_list,
                geometryType: 'MultiPolygon'
              };
            }
            resolve({
              ...districtObj,
              searchType: 'district'
            });
          });
        });
      });
    },
    drawPoint(data) {
      console.log('drawPoint', data);
      this.$bus.$emit('addPoint', data);
    },
    drawDistrict(data) {
      console.log('drawDistrict', data);
      this.$bus.$emit('addDistrict', data);
    }
  }
};
</script>

<style lang="less" scoped>
.location-search {
  width: 300px;
  /deep/.autocomplete-input {
    margin-right: 15px;
    width: 100%;

    .el-input--mini .el-input__inner {
      height: 36px;
      line-height: 36px;
      border-radius: 15px;
      background-color: #eee;
      border: none;
    }

    .el-input__prefix {
      margin-left: 5px;
      height: 36px;
      line-height: 36px;
    }
    .el-icon-location {
      text-align: right;
    }
  }
}

.item-container {
  display: flex;

  .item-right {
    flex: 1;
    text-align: right;
  }

  .item-left {
    max-width: 270px;
    .desc {
      font-size: 12px;
      color: #999;
    }
  }
}
</style>
